import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Lock, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeLogin() {
  const [employeeId, setEmployeeId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!employeeId || !pin) {
      toast.error('Please enter Employee ID and PIN');
      return;
    }
    if (pin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/employee/login`, {
        employee_id: employeeId,
        pin: pin
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      navigate('/employee/attendance');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/anmol-logo.jpg" 
            alt="Advanced Anmol Metcomp Pvt Ltd" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight text-zinc-900 mb-1" data-testid="employee-login-title">
            ATTENDANCE SYSTEM
          </h1>
          <p className="text-sm text-zinc-600 font-medium mb-1">Advanced Anmol Metcomp Pvt Ltd</p>
          <p className="text-xs text-zinc-500 font-medium" data-testid="employee-login-subtitle">Employee Login</p>
        </div>

        <div className="bg-white border-2 border-zinc-100 rounded-lg p-8 shadow-sm">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">
                Employee ID
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
                <input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className="w-full h-14 pl-12 pr-4 bg-white border-2 border-zinc-200 rounded-sm text-lg font-medium focus:border-zinc-900 focus:outline-none transition-colors placeholder:text-zinc-400"
                  placeholder="Enter your ID"
                  data-testid="employee-id-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-500 uppercase tracking-wider block mb-2">
                PIN
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
                <input
                  type={showPin ? "text" : "password"}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="w-full h-14 pl-12 pr-12 bg-white border-2 border-zinc-200 rounded-sm text-lg font-medium focus:border-zinc-900 focus:outline-none transition-colors placeholder:text-zinc-400"
                  placeholder="Enter 4-6 digit PIN"
                  maxLength={6}
                  data-testid="employee-pin-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  data-testid="toggle-pin-visibility"
                >
                  {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-14 bg-zinc-900 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="employee-login-button"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t-2 border-zinc-100 text-center">
            <button
              onClick={() => navigate('/hr/login')}
              className="text-sm text-zinc-600 hover:text-zinc-900 font-medium transition-colors"
              data-testid="hr-login-link"
            >
              HR/Management Login →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}